import React from "react";
import { Card } from "@/components/ui/card";

// Skeleton for service cards in CarWashList
export function SkeletonServiceCard() {
    return (
        <Card className="bg-white rounded-2xl shadow-sm p-4 space-y-2.5">
            <div className="skeleton h-6 w-3/4"></div>

            <div className="flex gap-4">
                <div className="skeleton w-28 h-28 rounded-xl flex-shrink-0"></div>

                <div className="flex-1 space-y-2">
                    <div className="skeleton h-4 w-full"></div>
                    <div className="skeleton h-4 w-5/6"></div>
                    <div className="flex items-center gap-2 mt-2">
                        <div className="skeleton h-4 w-16"></div>
                        <div className="skeleton h-4 w-12"></div>
                    </div>
                </div>
            </div>

            <div className="border-t border-dashed border-gray-300"></div>

            <div className="flex justify-between items-center">
                <div className="skeleton h-6 w-20"></div>
                <div className="skeleton h-10 w-20 rounded-full"></div>
            </div>
        </Card>
    );
}

// Skeleton for category chips
export function SkeletonChips({ count = 4 }) {
    return (
        <div className="flex gap-3 overflow-x-auto scrollbar-hide">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="skeleton h-10 w-32 rounded-2xl flex-shrink-0"></div>
            ))}
        </div>
    );
}

// Skeleton for tag chips (second row)
export function SkeletonTagChips({ count = 4 }) {
    return (
        <div className="flex gap-3 overflow-x-auto scrollbar-hide">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="skeleton h-9 w-40 rounded-full flex-shrink-0"></div>
            ))}
        </div>
    );
}

// Skeleton for header
export function SkeletonHeader() {
    return (
        <div className="flex justify-between items-center px-4 py-3 bg-white shadow-sm">
            <div className="flex items-center gap-1">
                <div className="flex flex-col gap-1">
                    <div className="skeleton h-5 w-24"></div>
                    <div className="skeleton h-4 w-16"></div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <div className="flex flex-col gap-1">
                    <div className="skeleton h-5 w-20"></div>
                    <div className="skeleton h-4 w-16"></div>
                </div>
                <div className="skeleton h-8 w-8 rounded-full"></div>
            </div>
        </div>
    );
}

// Skeleton for promo banners
export function SkeletonPromoBanner() {
    return (
        <div className="rounded-xl overflow-hidden h-40 mt-1">
            <div className="skeleton w-full h-40"></div>
        </div>
    );
}

// Skeleton for service grid (Home page)
export function SkeletonServiceGrid({ count = 7 }) {
    return (
        <div className="grid grid-cols-3 gap-6 mt-4">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="flex flex-col items-center text-center">
                    <div className="skeleton w-12 h-12 rounded-full"></div>
                    <div className="skeleton h-4 w-16 mt-2"></div>
                </div>
            ))}
        </div>
    );
}

// Skeleton for product slider
export function SkeletonProductSlider() {
    return (
        <div className="w-full overflow-x-auto snap-x snap-mandatory flex gap-4 px-0 py-3">
            <div className="skeleton min-w-full h-48 rounded-xl snap-start"></div>
        </div>
    );
}

// Skeleton for stats cards
export function SkeletonStatsCards() {
    return (
        <div className="grid grid-cols-2 gap-4 mt-6 mb-10">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white p-4 rounded-xl shadow text-center">
                    <div className="skeleton h-7 w-16 mx-auto"></div>
                    <div className="skeleton h-4 w-20 mx-auto mt-2"></div>
                </div>
            ))}
        </div>
    );
}

// Complete skeleton for CarWashList page
export function SkeletonCarWashList() {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Sticky header skeleton */}
            <div className="sticky top-0 z-50 bg-white border-b">
                <div className="max-w-4xl mx-auto px-4 py-3">
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className="skeleton w-10 h-10 rounded-full"></div>
                            <div className="skeleton h-6 w-20"></div>
                        </div>
                        <div className="skeleton h-10 w-24 rounded-xl"></div>
                    </div>

                    {/* Category chips */}
                    <SkeletonChips count={4} />
                </div>
            </div>

            {/* Content skeleton */}
            <div className="max-w-4xl mx-auto px-4 py-6">
                <div className="space-y-6">
                    {/* Section title */}
                    <div className="skeleton h-8 w-32"></div>

                    {/* Service cards */}
                    <div className="space-y-2">
                        <SkeletonServiceCard />
                        <SkeletonServiceCard />
                        <SkeletonServiceCard />
                    </div>
                </div>
            </div>
        </div>
    );
}

// Complete skeleton for Home page
export function SkeletonHome() {
    return (
        <div className="min-h-screen bg-[#000000] pb-24">
            <SkeletonHeader />
            <SkeletonProductSlider />

            <div className="bg-white px-4 py-5 shadow-sm rounded-lg">
                <div className="skeleton h-6 w-48 mb-4"></div>
                <SkeletonServiceGrid count={7} />
            </div>

            <SkeletonPromoBanner />

            <div className="px-4 mt-6">
                <div className="skeleton h-6 w-40 mb-2"></div>
                <div className="bg-white shadow rounded-xl p-5">
                    <div className="skeleton h-32 w-full"></div>
                </div>
                <SkeletonStatsCards />
            </div>
        </div>
    );
}

// Skeleton for Cart Address Card
export function SkeletonAddressCard() {
    return (
        <div className="flex items-center gap-3 px-4 py-3.5 bg-white mt-3 rounded-xl shadow-sm mx-4 border border-gray-100">
            <div className="skeleton w-9 h-9 rounded-full"></div>
            <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-32"></div>
                <div className="skeleton h-3 w-24"></div>
            </div>
        </div>
    );
}

// Skeleton for Cart Vehicle Card
export function SkeletonVehicleCard() {
    return (
        <div className="mx-4 mt-5 p-5 flex items-center shadow-sm border border-gray-100 rounded-xl bg-white">
            <div className="skeleton w-20 h-20 rounded-lg mr-4"></div>
            <div className="flex-1 space-y-2">
                <div className="skeleton h-5 w-32"></div>
                <div className="skeleton h-4 w-24"></div>
                <div className="skeleton h-4 w-40"></div>
            </div>
            <div className="skeleton w-10 h-10 rounded-full"></div>
        </div>
    );
}

// Skeleton for Add On Card
export function SkeletonAddOnCard() {
    return (
        <div className="min-w-[160px] flex flex-col border border-gray-200 shadow-sm rounded-2xl bg-white overflow-hidden">
            <div className="skeleton w-full h-28"></div>
            <div className="p-4 space-y-3">
                <div className="skeleton h-4 w-full"></div>
                <div className="skeleton h-5 w-16 mx-auto"></div>
                <div className="skeleton h-10 w-full rounded-xl"></div>
            </div>
        </div>
    );
}

// Skeleton for Frequency Package Card
export function SkeletonFrequencyCard() {
    return (
        <div className="min-w-[165px] bg-white rounded-xl shadow-sm border-2 border-gray-200 p-4">
            <div className="skeleton h-8 w-full rounded-lg mb-3"></div>
            <div className="skeleton h-4 w-20 mx-auto mb-2"></div>
            <div className="skeleton h-8 w-24 mx-auto mb-1"></div>
            <div className="skeleton h-3 w-16 mx-auto mb-3"></div>
            <div className="skeleton h-4 w-28 mx-auto mb-3"></div>
            <div className="skeleton h-10 w-full rounded-lg"></div>
        </div>
    );
}

// Complete skeleton for Cart page
export function SkeletonCart() {
    return (
        <div className="min-h-screen bg-[#F7F9FC] pb-32">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-4 bg-white shadow-sm sticky top-0 z-50">
                    <div className="skeleton w-10 h-10 rounded-full"></div>
                    <div className="skeleton h-6 w-24"></div>
                    <div className="w-10"></div>
                </div>

                {/* Address */}
                <SkeletonAddressCard />

                {/* Vehicle Card */}
                <SkeletonVehicleCard />

                {/* Price */}
                <div className="flex items-center gap-3 ml-4 mt-4">
                    <div className="skeleton h-8 w-20"></div>
                    <div className="skeleton h-4 w-16"></div>
                    <div className="skeleton h-6 w-16 rounded-full"></div>
                </div>

                {/* Discount Banner */}
                <div className="mx-4 mt-4">
                    <div className="skeleton h-12 w-full rounded-xl"></div>
                </div>

                {/* Add Ons Section */}
                <div className="ml-4 mt-7">
                    <div className="skeleton h-6 w-24 mb-1"></div>
                    <div className="skeleton h-4 w-40 mb-3"></div>
                </div>

                <div className="flex gap-3 px-4 mt-2 overflow-x-auto pb-2 scrollbar-hide">
                    <SkeletonAddOnCard />
                    <SkeletonAddOnCard />
                    <SkeletonAddOnCard />
                </div>

                {/* Frequency Packages */}
                <div className="mt-6 mx-4">
                    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                        <div className="skeleton h-12 w-full rounded-xl mb-5"></div>
                        <div className="skeleton h-6 w-32 mb-1"></div>
                        <div className="skeleton h-4 w-48 mb-4"></div>

                        <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide">
                            <SkeletonFrequencyCard />
                            <SkeletonFrequencyCard />
                            <SkeletonFrequencyCard />
                        </div>

                        <div className="skeleton h-4 w-64 mx-auto mt-4"></div>
                    </div>
                </div>

                {/* Add Another Vehicle */}
                <div className="mx-4 mt-6 p-4 flex items-center shadow-sm border border-gray-100 rounded-xl bg-white">
                    <div className="skeleton w-10 h-10 rounded-full mr-3"></div>
                    <div className="skeleton h-5 w-40 flex-1"></div>
                    <div className="skeleton h-10 w-20 rounded-xl"></div>
                </div>

                <div className="h-32"></div>
            </div>

            {/* Bottom Bar */}
            <div className="fixed bottom-0 left-0 w-full bg-white p-4 border-t shadow-xl">
                <div className="max-w-4xl mx-auto w-full px-4 flex justify-between items-center">
                    <div className="space-y-1">
                        <div className="skeleton h-6 w-16"></div>
                        <div className="skeleton h-3 w-20"></div>
                    </div>
                    <div className="skeleton h-12 w-32 rounded-xl"></div>
                </div>
            </div>
        </div>
    );
}

// Complete skeleton for Summary page
export function SkeletonSummary() {
    return (
        <div className="min-h-screen bg-slate-50 pb-24">
            {/* Header */}
            <div className="bg-white px-4 py-3 shadow-sm flex items-center gap-3 sticky top-0 z-10">
                <div className="skeleton w-6 h-6 rounded"></div>
                <div className="flex flex-col gap-1">
                    <div className="skeleton h-3 w-16"></div>
                    <div className="skeleton h-4 w-20"></div>
                </div>
            </div>

            <div className="p-4 space-y-4">
                {/* Vehicle Card */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                            <div className="skeleton w-16 h-10 rounded"></div>
                            <div className="skeleton h-5 w-32"></div>
                        </div>
                        <div className="skeleton w-8 h-8 rounded"></div>
                    </div>
                    <div className="border-t border-slate-100 pt-3">
                        <div className="skeleton h-4 w-24 mb-2"></div>
                        <div className="flex justify-between">
                            <div className="skeleton h-4 w-48"></div>
                            <div className="skeleton h-4 w-12"></div>
                        </div>
                    </div>
                </div>

                {/* Special Deal */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex justify-between mb-3">
                        <div className="skeleton h-5 w-32"></div>
                        <div className="skeleton h-5 w-24"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="skeleton h-32 rounded-lg"></div>
                        <div className="skeleton h-32 rounded-lg"></div>
                        <div className="skeleton h-32 rounded-lg"></div>
                    </div>
                    <div className="skeleton h-12 w-full rounded-lg"></div>
                </div>

                {/* Coupon */}
                <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-slate-100">
                    <div className="flex justify-between">
                        <div className="skeleton h-5 w-40"></div>
                        <div className="skeleton h-5 w-20"></div>
                    </div>
                </div>

                {/* Payment Summary */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="skeleton h-6 w-32 mb-3"></div>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <div className="skeleton h-4 w-24"></div>
                            <div className="skeleton h-4 w-12"></div>
                        </div>
                        <div className="flex justify-between">
                            <div className="skeleton h-4 w-32"></div>
                            <div className="skeleton h-4 w-12"></div>
                        </div>
                        <div className="flex justify-between">
                            <div className="skeleton h-4 w-28"></div>
                            <div className="skeleton h-4 w-12"></div>
                        </div>
                        <div className="border-t my-2"></div>
                        <div className="flex justify-between">
                            <div className="skeleton h-5 w-24"></div>
                            <div className="skeleton h-5 w-16"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Generic Skeleton Page for lazy loading fallbacks
export function SkeletonPage() {
    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header Skeleton */}
            <div className="bg-white shadow-sm sticky top-0 z-50">
                <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="skeleton w-8 h-8 rounded-full"></div>
                        <div className="flex flex-col gap-1">
                            <div className="skeleton h-3 w-20"></div>
                            <div className="skeleton h-4 w-24"></div>
                        </div>
                    </div>
                    <div className="skeleton w-8 h-8 rounded-full"></div>
                </div>
            </div>

            <div className="max-w-md mx-auto px-4 pt-4 space-y-6">
                {/* Hero/Banner Skeleton */}
                <div className="w-full h-40 rounded-2xl skeleton"></div>

                {/* Quick Actions/Categories Skeleton */}
                <div className="space-y-3">
                    <div className="flex justify-between items-center">
                        <div className="skeleton h-5 w-32"></div>
                        <div className="skeleton h-4 w-16"></div>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="flex flex-col items-center gap-2 min-w-[70px]">
                                <div className="skeleton w-14 h-14 rounded-full"></div>
                                <div className="skeleton h-3 w-12"></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Content List Skeleton */}
                <div className="space-y-4">
                    <div className="skeleton h-6 w-40"></div>
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex gap-4">
                            <div className="skeleton w-20 h-20 rounded-lg flex-shrink-0"></div>
                            <div className="flex-1 space-y-2">
                                <div className="skeleton h-4 w-3/4"></div>
                                <div className="skeleton h-3 w-1/2"></div>
                                <div className="flex justify-between items-center mt-2">
                                    <div className="skeleton h-4 w-16"></div>
                                    <div className="skeleton h-6 w-20 rounded-full"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
